import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Flame, RefreshCw, Loader2, AlertTriangle, Activity, Users, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BAND_PILL = {
  very_high: 'bg-red-50 text-red-700 border-red-200',
  high: 'bg-amber-50 text-amber-700 border-amber-200',
  moderate: 'bg-blue-50 text-blue-700 border-blue-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Users, label: 'Clinician Retention', href: '/admin/clinician-retention' },
  { icon: Flame, label: 'Burnout Pulse', active: true },
];

export default function AdminBurnoutPulse() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/burnout-pulse`, getAuthHeader());
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load burnout cohort');
    } finally { setLoading(false); }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const k = data?.kpis || {};
  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Workforce Burnout Pulse"
      subtitle="Continuous early-warning detection. Intervene at moderate scores — by the time someone hits 'very high' you've already lost them."
      testId="admin-burnout-pulse-page"
    >
      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}
      {!loading && data && (
        <>
          <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3">
            <Button onClick={load} className="cn-btn cn-btn-ghost !py-2" data-testid="burnout-refresh">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="burnout-kpis">
            <Tile label="Cohort" value={k.cohort_size} testid="burnout-kpi-total" />
            <Tile label="Very high" value={k.very_high} tone="red" testid="burnout-kpi-very-high" />
            <Tile label="High" value={k.high} tone="amber" testid="burnout-kpi-high" />
            <Tile label="Median score" value={k.median_score} testid="burnout-kpi-median" />
            <Tile label="Peer median wk appts" value={k.peer_median_weekly_appts} testid="burnout-kpi-peer-median" />
          </div>
          <div className="cn-card overflow-hidden" data-testid="burnout-cohort-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base">Clinicians ranked by burnout pulse</h4>
            </div>
            {(data.cohort || []).length === 0 ? (
              <div className="p-10 text-center" data-testid="burnout-empty">
                <p className="text-sm text-cn-text-muted">No active clinicians scored.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="burnout-cohort-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Clinician</th>
                      <th className="px-3 py-3 text-right">Score</th>
                      <th className="px-3 py-3">Band</th>
                      <th className="px-3 py-3 text-right">Appts last wk</th>
                      <th className="px-3 py-3 text-right">vs peer</th>
                      <th className="px-3 py-3 text-right">After-hours</th>
                      <th className="px-3 py-3 text-right">Days since PTO</th>
                      <th className="px-3 py-3">Drivers</th>
                      <th className="px-3 py-3">Top intervention</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(data.cohort || []).slice(0, 100).map((c) => (
                      <tr key={c.clinician_id} className="hover:bg-cn-bone-50/40 align-top" data-testid={`burnout-row-${c.clinician_id}`}>
                        <td className="px-5 py-2.5 font-medium">{c.clinician_name}</td>
                        <td className="px-3 py-2.5 text-right font-display font-bold text-base">{c.score}</td>
                        <td className="px-3 py-2.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${BAND_PILL[c.band]}`}>{c.band.replace('_', ' ')}</span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-xs">{c.appts_last_week}</td>
                        <td className={`px-3 py-2.5 text-right text-xs ${c.appts_vs_peer_pct > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{c.appts_vs_peer_pct}%</td>
                        <td className="px-3 py-2.5 text-right text-xs">{c.after_hours_share_pct}%</td>
                        <td className="px-3 py-2.5 text-right text-xs">{c.days_since_pto === null ? '—' : `${c.days_since_pto}d`}</td>
                        <td className="px-3 py-2.5 text-xs text-cn-text-muted">{(c.drivers || []).join(', ') || '—'}</td>
                        <td className="px-3 py-2.5 text-xs text-cn-text-muted max-w-xs">{(c.interventions || [])[0] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function Tile({ label, value, tone = 'neutral', testid }) {
  const cls = { neutral: 'text-cn-text', red: 'text-red-700', amber: 'text-amber-700' }[tone];
  return (
    <div className="cn-card p-4" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</p>
      <p className={`font-display font-bold text-2xl ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
